import { Encounter, EncounterStatus } from '@hb/shared'

// §1.6.1 version-chain mapping. `authentication.previousVersions[0]` is the original
// authentication; higher indices are later superseded amendments; the top-level
// `authentication` + `encounter.data` is the current version. The reason that produced
// version N is snapshotted into version N's own `formData.amendmentReason` on
// re-authentication — so the original never has a reason, and the current version's
// reason lives in `encounter.data.amendmentReason`.
export interface EncounterVersionEntry {
  /** 'original' = the first authentication; 'amendment' = a re-authentication */
  kind: 'original' | 'amendment'
  /** true for the live note (top-level authentication + encounter.data) */
  isCurrent: boolean
  authenticatedOn: number
  providerName: string
  /** The reason that produced this version — null for the original (or if not recorded) */
  reason: string | null
  /** Snapshot to render for superseded versions — null for the current version (the live note) */
  formData: Encounter['data'] | null
}

/**
 * Newest-first version history for an authenticated encounter. Empty for unamended
 * notes (no `previousVersions`), so unamended notes get no amendment chrome.
 */
export const getEncounterVersionHistory = (
  encounter: Pick<Encounter, 'data' | 'authentication'>,
): EncounterVersionEntry[] => {
  const auth = encounter.authentication
  if (!auth?.previousVersions?.length) return []
  const { previousVersions } = auth
  const entries: EncounterVersionEntry[] = [
    {
      kind: 'amendment',
      isCurrent: true,
      authenticatedOn: auth.authenticatedOn,
      providerName: `${auth.providerFirst} ${auth.providerLast}`,
      reason: encounter.data?.amendmentReason?.trim() || null,
      formData: null,
    },
  ]
  for (let i = previousVersions.length - 1; i >= 0; i -= 1) {
    const version = previousVersions[i]
    entries.push({
      kind: i === 0 ? 'original' : 'amendment',
      isCurrent: false,
      authenticatedOn: version.authenticatedOn,
      providerName: `${version.providerFirst} ${version.providerLast}`,
      reason: i === 0 ? null : version.formData?.amendmentReason?.trim() || null,
      formData: version.formData ?? null,
    })
  }
  return entries
}

/**
 * Whether EncounterView should render the amendment section: any recorded amendment,
 * or a pending amendment (post-authentication edits saved to draftData but not yet
 * re-authenticated).
 */
export const getShowEncounterAmendments = (
  encounter: Pick<Encounter, 'data' | 'authentication' | 'status' | 'draftData'>,
): boolean =>
  (encounter.authentication?.previousVersions?.length ?? 0) > 0 ||
  (encounter.status === EncounterStatus.AUTHENTICATED && !!encounter.draftData)
